import React, { useEffect, useState, useRef, useMemo } from 'react'
import { deleteScorecard, postScoreCard, getTournamentHoleSelection } from '../../api/tournamentAPI';
import swal from 'sweetalert2';
import { getPercentage, getStrokeExtraShot } from '../tournamentHelper';
import TournamentHolePlayerCheck from '../tournamentHolePlayerCheck.js';

const combineHolePoints = (points) => (points.length ? Math.max(...points) : 0);

function calculateHoleTotal(players) {

    const selected = players.filter(p => p.selected);
    const others = players
        .filter(p => !p.selected)
        .sort((a, b) => b.point - a.point);

    const selectedTotal = selected.reduce((s, p) => s + p.point, 0);

    const remainingSlots = Math.max(0, 3 - selected.length);

    const bestOtherTotal = others
        .slice(0, remainingSlots)
        .reduce((s, p) => s + p.point, 0);

    return selectedTotal + bestOtherTotal;
}

const FourballStableFordNet = ({
    tournamentDetails,
    tournamentDraw,
    tournamentRound,
    format,
    selectedPlayer,
    setScorecardStatus,
    scorecardStatus,
    gameDetails,
    closeDrawer,
    fetchTournamentLeaderboard,
    pointData,
    setShowPointModal
}) => {

    const [scores,              setScores]              = useState({});   // key: `${playerIndex}_${holeIndex}` -> raw input
    const [totalScores,         setTotalScores]         = useState([]);   // per-hole computed results (see calculateScores)
    const [perPlayerTotals,     setPerPlayerTotals]     = useState({});   // key: playerIndex -> { grossTotal, netTotal, pointsTotal }
    const [footerTotalScore,    setFooterTotalScore]    = useState('-');
    const [comment,              setComment]             = useState("");
    const [showHoleCheckModal,  setShowHoleCheckModal]  = useState(false);
    const [holeSelections,      setHoleSelections]      = useState({});   // key: hole -> { [playerNo]: true } => doubled

    const inputRefs = useRef({});

    // -- team players (1-4, only the ones that actually exist) -------------
    const players = useMemo(() => {
        if (!selectedPlayer) return [];
        return [1, 2, 3, 4].map(i => {
            const id = selectedPlayer[`Player${i}ID`];
            if (!id) return null; 

            const handicap = selectedPlayer[`Player${i}Handicap`];
            const gender   = selectedPlayer[`Player${i}Gender`];
            const name     = selectedPlayer[`Player${i}`] || selectedPlayer[`Player${i}Nick`] || `Player ${i}`;
            const type     = selectedPlayer[`Player${i}Type`];

            const calculatedHandicap = !!handicap && Math.round(getPercentage(handicap, gender === 'Female' ? tournamentDraw?.Lower : tournamentDraw?.Higher));

            const showStar = (gender === 'Female' && tournamentDraw?.Lower !== 100) || (gender !== 'Female' && tournamentDraw?.Higher !== 100);

            return { index: i, id, name, gender, handicap, calculatedHandicap, showStar, type };
        }).filter(Boolean);
    }, [selectedPlayer, tournamentDraw]);

    // -- hole-selection ("double point") lookup -----------------------------
    const loadHoleSelections = async () => {
        if (!tournamentDraw?.ID) return;
        try {
            const data = await getTournamentHoleSelection(tournamentDraw.ID);
            const map = {};
            (data || []).forEach(row => {
                if (!map[row.Hole]) map[row.Hole] = {};
                map[row.Hole][row.PlayerNo] = true;
            });
            setHoleSelections(map);
        } catch (err) {
            console.error('Could not load hole selections', err);
        }
    };

    useEffect(() => { loadHoleSelections(); }, [tournamentDraw]);

    // -- initialize the score grid for every player x hole ------------------
    useEffect(() => {
        if (!players.length || !gameDetails?.TeeBoxes?.ScoreCard) return;

        const initialScores = {};
        players.forEach(player => {
            gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
                const oldScore = (gameDetails.Card || []).find(
                    s => s.ScoreCardMasterID === item.ID && s.PlayerID === player.id
                );
                initialScores[`${player.index}_${index}`] = oldScore ? (oldScore.Scratch === 'Y' ? 'X' : oldScore.Score) : 0;
            });
        });
        setScores(initialScores);
    }, [gameDetails, players]);

    // -- recalculate whenever scores, players, or doubling info change ------
    useEffect(() => {
        if (players.length && Object.keys(scores).length) {
            calculateScores(scores);
        }
    }, [scores, players, holeSelections]);

    // -- point lookup ---------------------------------------------------------
    function getPointByScore(score) {
        if (!pointData || pointData.length === 0) return { name: '', point: 0 };

        const exact = pointData.find(e => e.Score === score);
        if (exact) return { name: exact.Name, point: exact.Point };

        // Outside the table range -> clamp to the nearest boundary row.
        let min = pointData[0];
        let max = pointData[0];
        pointData.forEach(e => {
            if (e.Score < min.Score) min = e;
            if (e.Score > max.Score) max = e;
        });

        return score < min.Score
            ? { name: min.Name, point: min.Point }
            : { name: max.Name, point: max.Point };
    }

    // -- core calculation: per player, per hole ------------------------------
    const calculateScores = (updatedScores) => {
        const updatedTotalScores = [];
        const aggPerPlayer = {};
        players.forEach(p => { aggPerPlayer[p.index] = { grossTotal: 0, netTotal: 0, pointsTotal: 0 }; });

        let cumulativeTotalScore = 0;
        let allScoresZero = true;

        gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
            const par = item.Par;
            const stroke = item.Stroke;

            const holePlayerResults = {};
            const holePoints = [];
            const playerPoints = [];
            let holeHasAnyScore = false;

            players.forEach(player => {
                const handicap = player.calculatedHandicap;
                const rawInput = updatedScores[`${player.index}_${index}`];
                const scoreInput = rawInput === 'X' ? 'X' : (parseInt(rawInput, 10) || 0);

                const extraShot = getStrokeExtraShot(stroke, handicap);
                const grossScore = scoreInput === 'X'
                    ? ((extraShot === 0 && handicap < 0) ? par + 2 : par + 2 + extraShot)
                    : scoreInput;

                let netScore = grossScore;
                let finalScore = null;
                let point = 0;
                let doubled = false;

                if (scoreInput !== 0) {
                    holeHasAnyScore = true;

                    if (handicap < 0) {
                        netScore = (stroke > (18 + handicap)) ? (grossScore + extraShot) : grossScore;
                    } else {
                        netScore = stroke <= handicap ? (grossScore - extraShot) : grossScore;
                    }

                    finalScore = scoreInput === 'X' ? netScore : (netScore - par);
                    point = getPointByScore(finalScore).point;

                    // Double this player's point if they selected this hole
                    // in the Hole Check grid (TournamentHolePlayerCheck).
                    doubled = !!holeSelections?.[item.HoleNoValue]?.[player.index];
                    if (doubled) point *= 2;

                    aggPerPlayer[player.index].grossTotal += grossScore;
                    aggPerPlayer[player.index].netTotal   += netScore;
                    aggPerPlayer[player.index].pointsTotal += point;
                    playerPoints.push({ point, selected: doubled });
                }

                holePlayerResults[player.index] = {
                    grossScore: scoreInput === 0 ? '-' : (scoreInput === 'X' ? 'X' : (finalScore === 0 ? 'E' : (finalScore < 0 ? finalScore : "+" + finalScore))),
                    netScore  : scoreInput === 0 ? '-' : netScore,
                    point     : scoreInput === 0 ? '-' : point,
                    doubled
                };
            });

            const holeTotalPoint = calculateHoleTotal(playerPoints);

            if (holeHasAnyScore) {
                allScoresZero = false;
                cumulativeTotalScore += holeTotalPoint;
            }

            updatedTotalScores[index] = {
                ...holePlayerResults,
                point      : holeHasAnyScore ? holeTotalPoint : '-',
                totalScore : holeHasAnyScore ? (cumulativeTotalScore === 0 ? 'E' : cumulativeTotalScore) : '-'
            };
        });

        setTotalScores(updatedTotalScores);
        setPerPlayerTotals(aggPerPlayer);
        setFooterTotalScore(allScoresZero ? '-' : cumulativeTotalScore);
    };

    const handleScoreChange = (playerIndex, holeIndex, value) => {
        if (!(/^[0-9X]*$/).test(value)) return;

        const key = `${playerIndex}_${holeIndex}`;
        setScores(prev => ({ ...prev, [key]: value === '' ? 0 : value }));

        // Focus the next relevant cell: next player on this hole, otherwise
        // the first player on the next hole.
        const currentPos = players.findIndex(p => p.index === playerIndex);
        let nextKey = null;
        if (currentPos !== -1 && currentPos < players.length - 1) {
            nextKey = `${players[currentPos + 1].index}_${holeIndex}`;
        } else if (players.length) {
            nextKey = `${players[0].index}_${holeIndex + 1}`;
        }
        const nextRef = nextKey && inputRefs.current[nextKey];
        if (nextRef) { nextRef.focus(); nextRef.select(); }
    };

    function onScratched(event, playerIndex, holeIndex) {
        const inputElement = event.target.closest('.input-group').querySelector('input');
        if (inputElement) {
            inputElement.value = 'X';
            handleScoreChange(playerIndex, holeIndex, 'X');
        }
    }

    const toggleStatus = (value) => {
        setScorecardStatus(prevStatus => (prevStatus == value ? '' : value));
    };

    // -- API actions ---------------------------------------------------------
    const deleteScoreCard = async () => {
        try {
            swal.fire({
                titleText: 'Loading', text: 'Please Wait...', showConfirmButton: false,
                onOpen: () => swal.showLoading()
            });
            await deleteScorecard(selectedPlayer.DrawID);
            fetchTournamentLeaderboard();
            swal.close();
            swal.fire({ icon: 'success', titleText: 'Scorecard Updated Successfully!', timer: 1500, animation: false, showConfirmButton: false });
            closeDrawer();
        } catch (err) {
            swal.close();
            swal.fire({ icon: 'error', titleText: 'Error!', text: err.message, buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
        }
    };

    const postScorecard = async (scoreCard) => {
        try {
            swal.fire({
                titleText: 'Loading', text: 'Please Wait...', showConfirmButton: false,
                onOpen: () => swal.showLoading()
            });
            await postScoreCard(scoreCard);
            fetchTournamentLeaderboard();
            swal.close();
            swal.fire({ icon: 'success', titleText: 'Scorecard Updated Successfully!', timer: 1500, animation: false, showConfirmButton: false });
            closeDrawer();
        } catch (err) {
            swal.close();
            swal.fire({ icon: 'error', titleText: 'Error!', text: err.message, buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
        }
    };

    const saveScorecard = () => {
        let newScoreCard = [];
        let notComplete = false;
        let hole = 0;

        gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
            const masterID = item.ID;
            const holeResult = totalScores[index];
            if (!holeResult) { notComplete = true; return; }

            let holeHasScore = false;

            players.forEach(player => {
                const playerResult = holeResult[player.index];
                const rawScore = scores[`${player.index}_${index}`];
                const extraShot = getStrokeExtraShot(item.Stroke, player.calculatedHandicap);

                let grossScore = rawScore;
                let Scratch = 'N';

                if (grossScore === 'X') {
                    Scratch = 'Y';
                    grossScore = (extraShot === 0 && player.calculatedHandicap < 0) ? (item.Par + 2) : (item.Par + 2 + extraShot);
                } else {
                    grossScore = parseInt(grossScore, 10) || 0;
                }

                if ((isNaN(grossScore) || grossScore === 0) && scorecardStatus === 'verify') {
                    notComplete = true;
                    return;
                }

                const netScore = playerResult?.netScore === '-' ? 0 : (parseInt(playerResult?.netScore, 10) || 0);
                const point    = playerResult?.point === '-' ? 0 : (parseInt(playerResult?.point, 10) || 0);
                const totalPoint = (holeResult.totalScore === '-' || holeResult.totalScore === 'E') ? 0 : (parseInt(holeResult.totalScore, 10) || 0);

                newScoreCard.push({
                    ID                : 0,
                    TournamentDrawID  : selectedPlayer.DrawID,
                    PlayerID          : player.id,
                    PlayerType        : player.type,
                    ScoreCardMasterID : masterID,
                    Score             : grossScore,
                    HandicapScore     : netScore,
                    DriveUserID       : 0,
                    Point             : point,
                    TotalPoint        : totalPoint,
                    Scratch
                });

                if (grossScore !== 0) holeHasScore = true;
            });

            if (holeHasScore) hole++;
        });

        if (notComplete) {
            swal.fire({ icon: "warning", titleText: "Incomplete Scorecard", text: "Please complete the scorecard..!" });
            return;
        }

        const result = {
            ID: 0,
            TournamentDrawID: selectedPlayer.DrawID,
            Hole: hole,
            Point: footerTotalScore === '-' ? 0 : footerTotalScore,
            TotalPoint: footerTotalScore === '-' ? 0 : footerTotalScore,
            IsVerified: scorecardStatus === 'verify' ? 'Y' : (scorecardStatus === 'disqualify' ? 'DQ' : (scorecardStatus === 'withdraw' ? 'WD' : 'N')),
            Comment: (scorecardStatus === 'disqualify' || scorecardStatus === 'withdraw') ? comment : ''
        };

        const dto = {
            tournamentId: tournamentDetails.ID,
            round: selectedPlayer.Round,
            userId: selectedPlayer.PlayerID,
            scoreCard: newScoreCard,
            result
        };
        postScorecard(dto);
    };

    return (
        <>
            <div className="kt-portlet kt-portlet--mobile padding-lr20">
                <div className="kt-portlet__head kt-portlet__head--lg  d-block padding-lr0 border-0">
                    <div id="roundInfoPanel" className="kt-notification margin-t10 mb-3" style={{ backgroundColor: "rgb(247, 248, 250)" }}>
                        <div className="kt-notification__item d-block">
                            <div className="row">
                                <div className="col-3">
                                    <div className="kt-user-card-v2 mb-2">
                                        <div className="kt-user-card-v2__pic">
                                            <img id="txtTournamentImg" src={tournamentDetails?.Icon} alt="tournament-logo" />
                                        </div>
                                        <div className="kt-user-card-v2__details ">
                                            <span id="txtTournament" className="kt-user-card-v2__name text-clip"> {tournamentDetails?.Name} </span>
                                            <span className="kt-user-card-v2__desc ">
                                                <span id="txtRound"> Round {selectedPlayer?.Round} </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-2 d-flex-center">
                                    <div className="kt-user-card-v2 ">
                                        <div className="kt-user-card-v2__details ">
                                            <span className="kt-user-card-v2__name ">Date </span>
                                            <span className="kt-user-card-v2__desc">
                                                <span id="txtStartDate">{moment(tournamentDetails?.StartDate).format('DD-MMM-YYYY')}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-2 d-flex-center">
                                    <div className="kt-user-card-v2">
                                        <div className="kt-user-card-v2__details ">
                                            <span className="kt-user-card-v2__name ">Format </span>
                                            <span className="kt-user-card-v2__desc ">
                                                <span id="txtFormat">{format?.Format}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {!!tournamentRound && tournamentRound?.length > 0 && (
                                    <div className="col-2 d-flex-center">
                                        <div className="kt-user-card-v2 mb-2">
                                            <div className="kt-user-card-v2__details ">
                                                <span className="kt-user-card-v2__name ">Tee Of Option </span>
                                                <span className="kt-user-card-v2__desc ">
                                                    <span id="txtTeeOption">{format?.TeeOffOption}</span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="col-1 d-flex-center">
                                    <div className="kt-user-card-v2 ">
                                        <div className="kt-user-card-v2__details ">
                                            <span className="kt-user-card-v2__name " id="lblStartTime">StartTime </span>
                                            <span className="kt-user-card-v2__desc ">
                                                <span id="txtStartTime">{selectedPlayer.Slot}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-2 d-flex-center justify-content-end">
                                    {!!pointData && pointData.length > 0 && (
                                        <button id="btnPoint" type="button" className="btn btn-secondary btn-elevate btn-pill btn-sm m-1"
                                            onClick={() => setShowPointModal(true)}>
                                            <i className="la la-list"></i> Point
                                        </button>
                                    )}
                                    <button id="btnHoleCheck" type="button" className="btn btn-secondary btn-elevate btn-pill btn-sm m-1"
                                        onClick={() => setShowHoleCheckModal(true)}>
                                        <i className="la la-check-square"></i> Holes
                                    </button>
                                </div>
                            </div>
                            <hr />

                            <div className="row">
                                <div className="col-9">
                                    <label className="kt-checkbox kt-checkbox--bold kt-checkbox--success mb-0">
                                        <input
                                            value="verify"
                                            name='verify'
                                            checked={scorecardStatus == 'verify'}
                                            onChange={() => toggleStatus('verify')}
                                            type="checkbox"
                                            className="form-verify" />
                                        Verify Scorecard <span></span>
                                    </label>
                                </div>
                                <div className="col-3 text-right">
                                    <div className="kt-checkbox-inline">
                                        <label className="kt-checkbox kt-checkbox--bold kt-checkbox--danger mb-0 mr-2">
                                            <input
                                                value="disqualify"
                                                name='disqualify'
                                                checked={scorecardStatus == 'disqualify'}
                                                onChange={() => toggleStatus('disqualify')}
                                                type="checkbox"
                                                className="form-disqualify" />
                                            Disqualify <span></span>
                                        </label>
                                        <label className="kt-checkbox kt-checkbox--bold kt-checkbox--brand mb-0">
                                            <input
                                                value="withdraw"
                                                checked={scorecardStatus == 'withdraw'}
                                                onChange={() => toggleStatus('withdraw')}
                                                name='withdraw'
                                                type="checkbox"
                                                className="form-withdraw" />
                                            Withdraw <span></span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {(scorecardStatus == 'withdraw' || scorecardStatus == 'disqualify') && (
                                <div className="row justify-content-end mt-2" id="pnlComment">
                                    <div className="col-4 text-right">
                                        <textarea
                                            name="txtComment"
                                            id="txtComment"
                                            className="form-control"
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            cols="3" rows="2" placeholder="Comments"
                                        ></textarea>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="table-register">

                        <table id="table-scorecard" className="table table-bordered table-striped table-card">
                            <thead>
                                <tr id="mainHeader">
                                    <th rowSpan="2" style={{ textAlign: 'center', width: '3%', verticalAlign: 'middle' }}>Hole   </th>
                                    <th rowSpan="2" style={{ textAlign: 'center', width: '3%', verticalAlign: 'middle' }}>Par    </th>
                                    <th rowSpan="2" style={{ textAlign: 'center', width: '3%', verticalAlign: 'middle' }}>Stroke </th>

                                    {players.map(player => (
                                        <th
                                            key={player.index}
                                            colSpan="3"
                                            style={{ textAlign: "center", width: `${100 / players.length}%`, verticalAlign: "middle" }}
                                        >
                                            <span className="mr-2">
                                                {player.name} - (
                                                {player.calculatedHandicap < 0 ? "+" + Math.abs(player.calculatedHandicap) : player.calculatedHandicap}
                                                )
                                                {player.showStar ? "*" : ""}
                                            </span>
                                        </th>
                                    ))}

                                    <th rowSpan="2" style={{ textAlign: 'center', width: '3%', verticalAlign: 'middle' }}>Total Points</th>
                                </tr>
                                <tr>
                                    {players.map(player => (
                                        <React.Fragment key={player.index}>
                                            <th className="text-center">Gross</th>
                                            <th className="text-center">Net</th>
                                            <th className="text-center">Points</th>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {gameDetails && gameDetails.TeeBoxes.ScoreCard.map((item, index) => {
                                    const totalScore = totalScores[index] || {};
                                    return (
                                        <tr key={`row_${item.HoleNoValue}`} className="row-score" data-masterid={item.ID}>
                                            <td style={{ textAlign: 'center' }}>{item.HoleNoValue}</td>
                                            <td style={{ textAlign: 'center' }}>{item.Par}</td>
                                            <td style={{ textAlign: 'center' }}>{item.Stroke}</td>

                                            {players.map(player => {
                                                const playerResult = totalScore[player.index] || {};
                                                return (
                                                    <React.Fragment key={player.index}>
                                                        <td className="player-td">
                                                            <div className="input-group">
                                                                <input
                                                                    className="form-control input-sm input-numeric"
                                                                    value={scores[`${player.index}_${index}`] || 0}
                                                                    ref={el => inputRefs.current[`${player.index}_${index}`] = el}
                                                                    onChange={(e) => handleScoreChange(player.index, index, e.target.value)}
                                                                />
                                                                <div className="input-group-append">
                                                                    <span
                                                                        className="input-group-text clickable"
                                                                        onClick={(e) => onScratched(e, player.index, index)}
                                                                    >
                                                                        <i className="la la-times"></i>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">{playerResult.netScore ?? '-'}</td>
                                                        <td className="text-center">
                                                            {playerResult.point ?? '-'}
                                                            {playerResult.doubled && <sup className="text-danger font-weight-bold"> x2</sup>}
                                                        </td>
                                                    </React.Fragment>
                                                );
                                            })}

                                            <td className="text-center">{totalScore.totalScore ?? '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="bg-light font-weight500">
                                    <td style={{ textAlign: 'center' }}>OUT</td>
                                    <td style={{ textAlign: 'center' }} id="lblTotalPar"> {gameDetails.TeeBoxes.Par} </td>
                                    <td style={{ textAlign: 'center' }}></td>
                                    {players.map(player => {
                                        const t = perPlayerTotals[player.index] || {};
                                        return (
                                            <React.Fragment key={player.index}>
                                                <td className="text-center">{t.grossTotal ?? '-'}</td>
                                                <td className="text-center">{t.netTotal ?? '-'}</td>
                                                <td className="text-center">{t.pointsTotal ?? '-'}</td>
                                            </React.Fragment>
                                        );
                                    })}
                                    <td style={{ textAlign: 'center' }}>{footerTotalScore}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
                <div className="kt-portlet__foot">
                    <div className="kt-form__actions">
                        <div className="row">
                            <div className="col-lg-12 ml-lg-auto text-center">
                                <button id="btnSave" type="button" className="btn btn-success d-inline-block mr-2" onClick={saveScorecard}> Save </button>
                                <button id="btnDelete" type="button" className="btn btn-danger d-inline-block" onClick={deleteScoreCard}> Delete </button>
                                <button id="btnBack" type="button" className="btn btn-secondary margin-l10" onClick={closeDrawer}> Close </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showHoleCheckModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Preferred Hole</h5>
                                <button type="button" className="close" aria-label="Close" onClick={() => { setShowHoleCheckModal(false); loadHoleSelections(); }}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <TournamentHolePlayerCheck
                                    tournamentDraw={tournamentDraw}
                                    selectedPlayer={selectedPlayer}
                                    closeDrawer={() => { setShowHoleCheckModal(false); loadHoleSelections(); }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default FourballStableFordNet;